"use client";

// THE VIEW CHIPS STAND IN THE NAVIGATOR (law_stage, 2026-10-12, John: "the view
// chips themselves need to stay intact when the UI collapses, so that anybody can
// quickly switch between the views even when they are in reader mode").
//
// A folder's views are drawn by the navigator itself. A panel's (activity, you,
// settings, connections, rooms, tasks, netti, the agents) are drawn by the panel, which
// knows its own views; this hands that row into the navigator's chip slot, where
// a folder's chips would stand, so it rides up with the fold and stays while the
// page is read. Off the stage (the old shell, a preview outside the shell) there
// is no slot and the row stands where it is written.
//
// ONE SHAPE (law_one_chip_row, 2026-10-16, John: "the way the view chips appear in
// the netti space is different from all the other view chips; on mobile there are a
// few places where the view chips have different heights. Align all of those").
// The panel passes its chips and nothing else: this draws the row around them, so a
// panel can no longer carry a row of its own (its own gap, its own wrap, its own
// phone override, its own height). The geometry is `.nav-chips` in globals.css,
// the folder's strip wears the same class (ReorderableChips), and a test fails on a
// panel that draws a row of chips without coming through here.

import { useCallback, type KeyboardEventHandler, type ReactNode, type Ref, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useOsShell } from "./OsShellContext";
import { useChipRow } from "./chipRow";

export type NavChipsProps = {
  /** the chips: `<button className="chip" data-on={…}>`, nothing else */
  children?: ReactNode;
  /** what the row is, said to a screen reader ("views of you") */
  label: string;
  /** the chips are tabs (a roving tabindex and arrow keys, the panel's own `onKeyDown`); otherwise a group of pressed buttons */
  tablist?: boolean;
  /** the row element, for a panel that moves focus along it */
  rowRef?: Ref<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  /** quiet words standing at the row's right end (the agents' "settings"); never a second row of chips */
  trailing?: ReactNode;
};

/** hand an element to a ref that is a callback or an object (the panel's own, beside ours) */
function seatRef<T>(ref: Ref<T> | undefined, el: T | null) {
  if (typeof ref === "function") ref(el);
  else if (ref) (ref as RefObject<T | null>).current = el;
}

export default function NavChips({ children, label, tablist = false, rowRef, onKeyDown, trailing }: NavChipsProps) {
  const { chipSlot } = useOsShell();
  const watch = useChipRow();
  const seat = useCallback(
    (el: HTMLDivElement | null) => {
      watch(el);
      seatRef(rowRef, el);
    },
    [watch, rowRef],
  );
  const row = (
    <div className="nav-chips-wrap">
      <div ref={seat} className="nav-chips" role={tablist ? "tablist" : "group"} aria-label={label} data-noswipe data-hscroll onKeyDown={onKeyDown}>
        {children}
      </div>
      {trailing}
    </div>
  );
  return chipSlot ? createPortal(row, chipSlot) : row;
}
